import { useCallback } from 'react';

import { ContactProps } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';

import { ModalTab } from '../models/modalTabs';

const contactFieldsToUpdate: Array<keyof PartnerProps> = ['primaryContactId', 'shippingContactId'];

type SubmitFn = (args: {
  partnerInfoLocal: PartnerProps;
  shippingContact: ContactProps;
  fieldsToUpdate?: Array<keyof PartnerProps>;
  onUpdatedPartner: (updatedPartner: PartnerProps) => void;
  onClose?: () => void;
  onMissingShippingFields?: () => void;
}) => Promise<void>;

type Args = {
  submit: SubmitFn;
  partnerInfoLocal: PartnerProps;
  shippingContact: ContactProps;
  setShowAlert: (show: boolean) => void;
  setSavingTab: (tab: ModalTab | null) => void;
  onUpdatedPartner: (updatedPartner: PartnerProps) => void;
  markTabClean: (tab: ModalTab) => void;
  markTabSaved: (tab: ModalTab) => void;
  markTabError: (tab: ModalTab) => void;
};

export function useContactsTabSave({
  submit,
  partnerInfoLocal,
  shippingContact,
  setShowAlert,
  setSavingTab,
  onUpdatedPartner,
  markTabClean,
  markTabSaved,
  markTabError,
}: Args) {
  return useCallback(async () => {
    setShowAlert(false);
    setSavingTab('contacts');

    let hasShippingValidationError = false;

    try {
      await submit({
        partnerInfoLocal,
        shippingContact,
        fieldsToUpdate: contactFieldsToUpdate,
        onUpdatedPartner,
        onMissingShippingFields: () => {
          hasShippingValidationError = true;
          setShowAlert(true);
        },
      });

      if (hasShippingValidationError) {
        markTabError('contacts');
        return;
      }

      markTabClean('contacts');
      markTabSaved('contacts');
    } catch {
      markTabError('contacts');
    } finally {
      setSavingTab(null);
    }
  }, [
    markTabClean,
    markTabError,
    markTabSaved,
    onUpdatedPartner,
    partnerInfoLocal,
    setSavingTab,
    setShowAlert,
    shippingContact,
    submit,
  ]);
}
